import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import '../../styles/AdminDashboard.css';

function rate(value) {
  if (value == null) return '—';
  return `${Math.round(Number(value) * 100)}%`;
}

export default function AdminCampaignsPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [occasion, setOccasion] = useState('');
  const [channel, setChannel] = useState('other');
  const [quantity, setQuantity] = useState(25);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const res = await adminApi.listCardCampaigns();
    setCampaigns(res.data || []);
  }, []);

  useEffect(() => {
    registerShellMeta?.({ title: 'Campaigns', subtitle: 'Batches, occasions and activation rate' });
    setRefreshHandler?.(load);
    load().catch((err) => setError(err.message));
  }, [load, registerShellMeta, setRefreshHandler]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const campaign = await adminApi.createCardCampaign({
        name,
        occasion,
        channel,
        targetQuantity: Number(quantity) || null,
      });
      if (quantity > 0 && campaign.data?._id) {
        await adminApi.generateCardBatch({
          quantity: Number(quantity),
          source: 'promo',
          campaignId: campaign.data._id,
          label: name,
        });
      }
      setName('');
      setOccasion('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="analytics-section">
        <div className="admin-table-header">
          <div>
            <h2>Card campaigns</h2>
            <p className="admin-table-subtitle">
              Create a named batch, generate stock, and see how many cards were handed out and claimed.
            </p>
          </div>
        </div>

        {error ? <p role="alert">{error}</p> : null}

        <form className="admin-inperson-distribution-form" onSubmit={create}>
          <label>
            Campaign name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Occasion
            <input value={occasion} onChange={(e) => setOccasion(e.target.value)} placeholder="Launch, conference…" />
          </label>
          <label>
            Channel
            <select value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="other">Other</option>
              <option value="event">Event</option>
              <option value="conference">Conference</option>
              <option value="direct_gift">Direct gift</option>
              <option value="ambassador">Ambassador</option>
              <option value="partner">Partner</option>
              <option value="sample">Sample</option>
              <option value="friends_family">Friends / family</option>
              <option value="inbound_request">Inbound request</option>
            </select>
          </label>
          <label>
            Cards to generate
            <input
              type="number"
              min="0"
              max="500"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? 'Creating…' : 'Create campaign'}
          </button>
        </form>

        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Occasion</th>
                <th>Issued</th>
                <th>Distributed</th>
                <th>Claimed</th>
                <th>Activation</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={6}>No campaigns yet.</td>
                </tr>
              ) : (
                campaigns.map((c) => (
                  <tr key={c._id}>
                    <td>{c.name}</td>
                    <td>{c.occasion || '—'}</td>
                    <td>{c.stats?.issued ?? 0}</td>
                    <td>{c.stats?.distributed ?? 0}</td>
                    <td>{c.stats?.claimed ?? 0}</td>
                    <td>{rate(c.stats?.activationRate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
